import React from "react";
import {
  Dialog,
  Toolbar,
  IconButton,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { List } from "@mui/material";
import { SelfHostedEditor } from "../components/TextEditor";
import { ImageCropField } from "../components/ImageCropField";
import { ImageCropState } from "../hooks/useImageCrop";

interface EditorState {
  content: string;
  contentGer: string;
}

type Props = {
  open: boolean;
  isEditApi: boolean;
  loader: boolean;
  Guides: any;
  errors: any;
  editorState: EditorState;
  imageCropEn: ImageCropState;
  imageCropGer: ImageCropState;
  onClose: () => void;
  onhandleChange: (e: any) => void;
  setEditorState: React.Dispatch<React.SetStateAction<EditorState>>;
  onSave: () => void;
  onRemoveImageEn?: () => void;
  onRemoveImageGer?: () => void;
};

export const GuidesFormDialog = ({
  open,
  isEditApi,
  loader,
  Guides,
  errors,
  imageCropEn,
  imageCropGer,
  onClose,
  onhandleChange,
  setEditorState,
  onSave,
  onRemoveImageEn,
  onRemoveImageGer,
}: Props) => {
  return (
    <Dialog fullScreen open={open}>
      <Toolbar>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <List>
        <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Deutsch
              </Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                English
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Titel</Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                id="title_ger"
                name="title_ger"
                value={Guides?.title_ger}
                onChange={(e) => onhandleChange(e)}
              />
              <Typography variant="caption" color="error">
                {errors["title_ger"]}
              </Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                id="title"
                name="title"
                value={Guides?.title}
                onChange={(e) => onhandleChange(e)}
              />
              <Typography variant="caption" color="error">
                {errors["title"]}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Untertitel</Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                id="subTitle_ger"
                name="subTitle_ger"
                value={Guides?.subTitle_ger}
                onChange={(e) => onhandleChange(e)}
              />
              <Typography variant="caption" color="error">
                {errors["subTitle_ger"]}
              </Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                id="subTitle"
                name="subTitle"
                value={Guides?.subTitle}
                onChange={(e) => onhandleChange(e)}
              />
              <Typography variant="caption" color="error">
                {errors["subTitle"]}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Bild</Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <ImageCropField
                inputId="image_ger"
                imageCrop={imageCropGer}
                existingImage={typeof Guides?.image_ger === "string" ? Guides?.image_ger : undefined}
                error={errors["image_ger"]}
                onRemoveExistingImage={onRemoveImageGer}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <ImageCropField
                inputId="imageUpload"
                imageCrop={imageCropEn}
                existingImage={typeof Guides?.image === "string" ? Guides?.image : undefined}
                error={errors["image"]}
                onRemoveExistingImage={onRemoveImageEn}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
              Beschreibung
            </Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <SelfHostedEditor
                onChange={(_event: any, editor: any) => {
                  setEditorState((prev) => ({
                    ...prev,
                    contentGer: editor?.getContent(),
                  }));
                }}
                initialValue={Guides?.desc_ger}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <SelfHostedEditor
                onChange={(_event: any, editor: any) => {
                  setEditorState((prev) => ({
                    ...prev,
                    content: editor?.getContent(),
                  }));
                }}
                initialValue={Guides?.desc}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {loader ? (
            <Button variant="contained" disabled>
              {isEditApi ? "Speichern" : "Hinzufügen"}
              <CircularProgress size={20} sx={{ ml: 2 }} />
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={onSave}
            >
              {isEditApi ? "Speichern" : "Hinzufügen"}
            </Button>
          )}
        </Box>
      </List>
    </Dialog>
  );
};
